import { createMemo, createSignal, Match, Show, Switch, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { regNoSearchQuery } from '../../graphql/queries/registration-number-check';
import { StyledVerticalSpace } from '../layout/layout.styles';
import { getRegNoStatusForProduct } from '../product-categories/product-link';
import {
    getProductStatus,
    getStatusRelatedElements,
    RegistrationNumberCheckItemType,
} from '../regcheck-search-results/regcheck-search-results-item/regcheck-search-results-item';
import { SkeletonLoader } from '../skeleton-loader/skeleton-loader';
import { Tabs } from '../tabs/tabs';
import { TabsOption } from '../tabs/tabs-types';
import { ProductEffectSchema } from './product-effect-schema';
import { ProductInfo } from './product-info';
import type { ProductProps } from './product-types';
import { StyledProductContainer, StyledProductSkeletons } from './products.styles';
import { RegNoItem } from './regno-item';
import { Upmas } from './upma/upmas';

export const Product = (props: ProductProps) => {
    const { graphQlClient } = useContext(AppContext);

    const [view, setView] = createSignal('info');
    const [originProductStatus, setOriginProductStatus] = createSignal<null | RegNoItem>(null);

    const product = () => props.productData()?.product;
    const hasUpmas = () => product()?.registrationNumbers?.some((regNo) => regNo?.upmas?.length);

    createMemo(async () => {
        if (!product()?.originProductRegistrationNumber) {
            return;
        }

        const response = await graphQlClient(regNoSearchQuery, { searchTerm: product()?.originProductRegistrationNumber });
        const data: RegistrationNumberCheckItemType[] = response?.regNoSearch || [];

        const hit = data.find((item) => item?.regNumber === product()?.originProductRegistrationNumber);
        if (hit) {
            const status = getProductStatus(hit);

            setOriginProductStatus({
                ...hit,
                regNumber: product()?.originProductRegistrationNumberAlias || hit.regNumber,
                ...getStatusRelatedElements(status),
            });
        }
    });

    const tabOptions = (): TabsOption[] => [
        {
            value: 'info',
            text: 'Info',
        },
        {
            value: 'effects',
            text: 'Effektschema',
            disabled: !product()?.effects?.length,
        },
        {
            value: 'upma',
            text: 'UPMA',
            disabled: !hasUpmas(),
        },
    ];

    return (
        <StyledProductContainer>
            <Show when={props.showHeader}>
                <h3>
                    <Show
                        when={product()?.originProductRegistrationNumber}
                        fallback={(() => {
                            const status = getRegNoStatusForProduct(product()?.name, props?.regNos?.() || []);
                            return status?.Icon ? <status.Icon /> : null;
                        })()}
                    >
                        {(() => {
                            const Icon = originProductStatus()?.Icon;
                            return Icon ? <Icon /> : null;
                        })()}
                    </Show>
                    {product()?.name}
                </h3>
            </Show>
            <Show when={!props.productData()?.product.children?.length}>
                <Tabs options={tabOptions()} value={view()} onChange={(val: string) => setView(val)} />
            </Show>
            <StyledVerticalSpace size={1} />
            <Show
                keyed
                when={props.productData()?.product}
                fallback={
                    <StyledProductSkeletons>
                        <SkeletonLoader numBlocks={1} />
                        <SkeletonLoader numBlocks={2} height={105} />
                    </StyledProductSkeletons>
                }
            >
                {(product) => (
                    <Switch>
                        <Match when={view() === 'info'}>
                            <ProductInfo product={product} regNos={props.regNos} originProductStatus={originProductStatus()} />
                        </Match>
                        <Match when={view() === 'effects'}>
                            <Show when={product?.effects.length} fallback={<p>Inga effekter hittades...</p>}>
                                <ProductEffectSchema product={product} />
                            </Show>
                        </Match>
                        <Match when={view() === 'upma'}>
                            <Upmas registrationNumbers={product?.registrationNumbers || []} />
                        </Match>
                    </Switch>
                )}
            </Show>
        </StyledProductContainer>
    );
};
